import React, { useContext, useEffect, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

import { Paper, IconButton, useMediaQuery, useTheme } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import useUiMode from "../../hooks/useUiMode";
import useTicketStyles from "./ticketStyles";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useAuditMode } from "../../context/AuditMode/AuditModeContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import toastError from "../../errors/toastError";
import { getTicketByUuid, markTicketRead } from "../../services/tickets.api";
import ContactProfile360Modal from "../ContactProfile360Modal";
import IaChatBriefingBanner from "../IaChatBriefingBanner";
import useTicketSocket from "./useTicketSocket";
import MessageInput from "../MessageInputCustom/";
import MessagesList from "../MessagesList";
import TicketActionButtons from "../TicketActionButtonsCustom";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import OtherThreadsNotice from "../OtherThreadsNotice";
import { TagsContainer } from "../TagsContainer";


const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useTicketStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isSimple } = useUiMode();

  const { user } = useContext(AuthContext);
  const { auditActive } = useAuditMode();

  const [profile360Open, setProfile360Open] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [isDragActive, setIsDragActive] = useState(false);
  const [pendingDropFiles, setPendingDropFiles] = useState([]);
  const isMounted = useRef(true);
  const ticketRef = useRef(ticket); // ✅ REFERENCIA PARA ACCEDER AL TICKET ACTUAL EN LISTENERS

  const socketManager = useContext(SocketContext);
  
  // ✅ FIX 05/12/2025: UN SOLO useEffect controla isMounted cuando el componente se monta/desmonta
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // ✅ Sin dependencias = solo al montar/desmontar el componente
  
  // ✅ ACTUALIZAR REFERENCIA CUANDO EL TICKET CAMBIA
  useEffect(() => {
    ticketRef.current = ticket;
  }, [ticket]);

  useEffect(() => {
    // ✅ Guard: evita GET /tickets/u/undefined (404) cuando el param del router aún no está listo.
    if (!ticketId) {
      return;
    }
    // ✅ FIX 05/12/2025: NO modificar isMounted aquí, solo leerlo
    // 0297 Fase A: sin demora artificial previa a GET /tickets/u/ (antes 500 ms encadenaba con MessagesList).
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          /**
           * QUIÉN PUEDE ABRIR ESTE TICKET LO DECIDE EL SERVIDOR.
           *
           * 🔴 Aquí vivía una TERCERA copia de la regla de alcance, y estaba mal
           * de dos maneras:
           *
           *   const queueAllowed = queueId === null || queues.find(q => q.id === queueId);
           *
           *   1. miraba `user.queues` —la FICHA—, así que a quien recibe su
           *      departamento por EQUIPO lo echaba;
           *   2. ni siquiera comprobaba si el ticket era SUYO: Paquita, dueña de
           *      sus propios tickets, no podía abrirlos (QA 2026-08-23).
           *
           * Y era la única defensa que había: `GET /tickets/u/:uuid` no
           * comprobaba ni la empresa. Ahora el servidor autoriza con la misma
           * regla que gobierna los sockets y los mensajes, y responde 403; aquí
           * solo se traduce ese 403 a un mensaje y una vuelta a la bandeja.
           */
          const { data } = await getTicketByUuid(ticketId);

          if (isMounted.current) {
            setContact(data.contact);
            setTicket(data);
            setLoading(false);
          }

          // 0297 Fase B.2: marcar leído sin await — no compite en la cola con GET /messages tras el paint.
          markTicketRead(data.id)
            .catch((error) => {
              console.warn("⚠️ ERROR AL MARCAR TICKET COMO LEÍDO:", error);
            });
        } catch (err) {
          if (isMounted.current) {
            setLoading(false);
            const code = err?.response?.data?.error || err?.response?.data?.code;
            if (code === "ERR_NO_TICKET_FOUND") {
              toast.info(i18n.t("backendErrors.ERR_NO_TICKET_FOUND"));
              history.push("/tickets");
              return;
            }
            // El servidor denegó el acceso: mismo aviso de siempre y vuelta a la
            // bandeja, pero ahora la decisión la tomó quien puede tomarla.
            if (err?.response?.status === 403 || code === "ERR_NO_PERMISSION") {
              toast.error(i18n.t("tickets.accessDenied"));
              history.push("/tickets");
              return;
            }
            toastError(err);
          }
        }
      };
      fetchTicket();
    }, 0);
    return () => {
      clearTimeout(delayDebounceFn);
      // ✅ FIX 05/12/2025: NO modificar isMounted aquí, solo leerlo
    };
  }, [ticketId, user, history]);

  useTicketSocket({
    socketManager,
    ticketId,
    isMounted,
    ticketRef,
    setTicket,
    setContact,
    history,
  });

  const openProfile360 = () => {
    if (contact?.id) setProfile360Open(true);
  };

  const handleTicketDragEnter = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleTicketDragOver = (e) => {
    e.preventDefault();
  };

  const handleOverlayDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragActive(false);
    }
  };

  const handleOverlayDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setPendingDropFiles(files);
    }
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={openProfile360}
        />
      );
    }
  };

  const renderMessagesList = () => {
    // ✅ CORRECCIÓN: Solo renderizar MessagesList cuando el ticket esté completamente cargado
    // Esto previene que el componente se monte con ticketId undefined
    if (!ticket?.id) {
      return null;
    }
    
    return (
      <>
        <MessagesList
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
        ></MessagesList>
        <IaChatBriefingBanner contact={contact} ticket={ticket} onOpenProfile360={openProfile360} />
        {!auditActive && (
          <MessageInput
            ticketId={ticket.id}
            ticketStatus={ticket.status}
            ticket={ticket}
            pendingDropFiles={pendingDropFiles}
            onPendingDropFilesHandled={() => setPendingDropFiles([])}
          />
        )}
      </>
    );
  };

  return (
    <div className={classes.root} id="ticket-panel-root">
      <Paper
        variant="outlined"
        elevation={0}
        className={classes.mainWrapper}
        onDragEnter={handleTicketDragEnter}
        onDragOver={handleTicketDragOver}
      >
        {isDragActive && (
          <div
            onDragOver={handleTicketDragOver}
            onDragLeave={handleOverlayDragLeave}
            onDrop={handleOverlayDrop}
            style={{
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 500,
              backgroundColor: "rgba(37, 211, 102, 0.10)",
              border: "3px dashed #25D366",
              borderRadius: 4,
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "all",
            }}
          >
            <div style={{ textAlign: "center", color: "#25D366", userSelect: "none" }}>
              <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 16 }}>📎</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>Suelta aquí para adjuntar</div>
            </div>
          </div>
        )}
        {/* Solo TicketHeader con z-index 600 — TagsContainer queda bajo el preview */}
        <div style={{ position: "relative", zIndex: 600, flexShrink: 0 }}>
          <TicketHeader loading={loading}>
            {isSimple && isMobile && (
              <IconButton
                aria-label={i18n.t("tickets.simple.backToList")}
                onClick={() => history.push("/tickets")}
                style={{ alignSelf: "center", marginLeft: 4 }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {renderTicketInfo()}
            {!auditActive && <TicketActionButtons ticket={ticket} />}
          </TicketHeader>
        </div>
        {ticket?.id && !loading && (
          <OtherThreadsNotice ticketId={ticket.id} />
        )}
        {ticket?.id && !loading && (
          <TagsContainer ticket={ticket} />
        )}
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      </Paper>
      <ContactProfile360Modal
        open={profile360Open}
        onClose={() => setProfile360Open(false)}
        contactId={contact?.id}
        onContactUpdated={async () => {
          if (!ticketId) return;
          try {
            const { data } = await getTicketByUuid(ticketId);
            if (isMounted.current) setContact(data.contact || {});
          } catch (_) {
            /* noop */
          }
        }}
      />
    </div>
  );
};

export default Ticket;
